import { useState, useEffect, useRef, type ComponentType } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  Loader,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { notifications } from "@mantine/notifications";
import TitleComponent from "@/components/shared/TitleComponent";
import { successNotification } from "@/shared/utils/successNotification";
import { errorNotification } from "@/shared/utils/errorNotification";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconBolt,
  IconCheck,
  IconCreditCard,
  IconInfoCircle,
  IconShieldCheck,
  IconSparkles,
  IconStack2,
  IconStar,
  IconTrendingUp,
} from "@tabler/icons-react";
import { useUserContext } from "@/context/UserContext";
import { formatPrice, getCurrencyForCountry } from "@/shared/utils/formatCurrency";

interface PaymentConfig {
  creditPriceInr: number;
  creditPriceUsd: number;
  verificationFeeCredits: number;
  engagementFeeCredits: number;
}

type PackIconProps = { size?: number; stroke?: number };

interface CreditPackDef {
  id: string;
  credits: number;
  tierTitle: string;
  subtitle: string;
  icon: ComponentType<PackIconProps>;
  featured: boolean;
}

const TOPUP_PACK_TOTAL_MAJOR: Record<20 | 50 | 100, { INR: number; USD: number }> = {
  20: { INR: 10_000, USD: 99 },
  50: { INR: 23_000, USD: 227.7 },
  100: { INR: 40_000, USD: 399 },
};

function getPackTotalMajor(credits: number, cur: "INR" | "USD"): number {
  const row = TOPUP_PACK_TOTAL_MAJOR[credits as 20 | 50 | 100];
  return row ? row[cur] : 0;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const cleanupRazorpay = () => {
  const scripts = document.querySelectorAll("script");
  scripts.forEach((script) => {
    if (script.src.includes("razorpay.com")) {
      script.remove();
    }
  });
  const containers = document.querySelectorAll(".razorpay-container");
  containers.forEach((container) => container.remove());
  if (window.Razorpay) {
    (window as any).Razorpay = undefined;
  }
};

const iconBoxGreen = { backgroundColor: "#DCFCE7", color: "#16A34A" };

const freeBadge = (
  <Badge color="green" variant="filled" size="sm" radius="xl" tt="none" fw={600} c="#fff" style={{ letterSpacing: "0.02em" }}>
    FREE
  </Badge>
);

function CreditBullet({ color }: { color: string }) {
  return (
    <Box
      w={6}
      h={6}
      style={{
        borderRadius: 999,
        backgroundColor: color,
        flexShrink: 0,
        marginTop: 6,
      }}
    />
  );
}

type UsageRow =
  | {
      key: string;
      title: string;
      description: string;
      kind: "free";
      strikeCredits?: number;
    }
  | { key: string; title: string; description: string; kind: "paid"; credits: number };

function CreditsUsageExplainer({
  verificationCredits,
  engagementCredits,
}: {
  verificationCredits: number;
  engagementCredits: number;
}) {
  const totalJourney = verificationCredits + engagementCredits;

  const rows: UsageRow[] = [
    {
      key: "profile",
      title: "Company Profile Creation",
      description: "AI-generated profile setup for your company.",
      kind: "free",
      strikeCredits: 2,
    },
    {
      key: "verification",
      title: "Company Verification & Activation",
      description: "Identity verification and account activation.",
      kind: "paid",
      credits: verificationCredits,
    },
    {
      key: "discovery",
      title: "Discovery & Search",
      description: "Browse and find potential partners.",
      kind: "free",
      strikeCredits: 3,
    },
    {
      key: "express",
      title: "Express Interest",
      description: "Signal interest in a company.",
      kind: "free",
    },
    {
      key: "mutual",
      title: "Mutual Interest Confirmation",
      description: "Confirm when both sides are interested.",
      kind: "free",
    },
    {
      key: "engagement",
      title: "Engagement Activation",
      description: "Unlock verified connection with a partner company.",
      kind: "paid",
      credits: engagementCredits,
    },
    {
      key: "nda",
      title: "NDA Execution",
      description: "Mutual NDA signing between companies.",
      kind: "free",
      strikeCredits: 7,
    },
    {
      key: "compat",
      title: "Compatibility Assessment Workspace (Optional)",
      description: "Structured compatibility evaluation.",
      kind: "free",
    },
    {
      key: "snapshot",
      title: "Company Background Snapshot",
      description: "Auto-generated company overview report.",
      kind: "free",
    },
    {
      key: "reverification",
      title: "Reverification",
      description: "Periodic identity re-check.",
      kind: "free",
    },
  ];

  const legacyJourneyTotalStrikethrough = 21;

  const paidPlanFeatures = [
    "Everything in Free Discovery",
    "Full company details revealed",
    "Direct contact information",
    "Profile access workspace",
    "Auto-advanced API",
    "Secured document sharing",
    "Protected messaging",
    "Priority both & swift trust",
    "Collaboration invitation",
  ];

  const platformFreeFeatures = [
    "AI Generated company profile creation",
    "Compatibility for collaboration assessment between two companies",
    "NDA execution between companies",
    "Proposal exchange",
    "Platform discovery and exploration",
    "Complete report of the collaboration workspace",
  ];

  const creditsCell = (row: UsageRow) => {
    if (row.kind === "paid") {
      return (
        <Flex justify="center" align="center" gap={8} wrap="nowrap">
          <CreditBullet color="var(--mantine-color-primary-6)" />
          <Text fz="sm" fw={600} c="primary.7">
            {row.credits} Credit{row.credits !== 1 ? "s" : ""}
          </Text>
        </Flex>
      );
    }
    return (
      <Flex justify="center" align="center" gap="sm" wrap="wrap">
        {row.strikeCredits != null ? (
          <Text fz="sm" fw={500} c="slate.5" td="line-through">
            {row.strikeCredits}
          </Text>
        ) : null}
        {freeBadge}
      </Flex>
    );
  };

  return (
    <Box maw={1100} mx="auto" mt="xl" pb="md">
      <Paper radius={12} withBorder p={0} style={{ borderColor: "var(--mantine-color-slate-3)", overflow: "hidden" }}>
        <Box
          px="lg"
          py="md"
          style={{
            background: "linear-gradient(180deg, #EEF2FF 0%, var(--mantine-color-primary-0) 100%)",
            borderBottom: "1px solid var(--mantine-color-slate-2)",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Flex gap="md" align="flex-start" wrap="nowrap">
            <Box
              w={48}
              h={48}
              style={{
                borderRadius: 10,
                background: "linear-gradient(135deg, #FBBF24 0%, #FBBF24 42%, #2563EB 42%, #1E40AF 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              }}
            >
              <IconCreditCard size={24} stroke={1.5} color="#fff" />
            </Box>
            <Box style={{ minWidth: 0 }}>
              <Text fz="md" fw={700} c="primary.8">
                How Credits Are Used — Action-by-Action
              </Text>
              <Text fz="sm" c="slate.6" mt={6} lh={1.5}>
                Only two actions consume credits. Everything else on the platform is completely free.
              </Text>
            </Box>
          </Flex>
        </Box>

        <Table
          verticalSpacing="md"
          horizontalSpacing="lg"
          withTableBorder
          withColumnBorders
          styles={{
            table: { borderCollapse: "collapse" as const },
            th: {
              backgroundColor: "var(--mantine-color-slate-1)",
              color: "var(--mantine-color-slate-8)",
              fontSize: "var(--mantine-font-size-xs)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              borderColor: "var(--mantine-color-slate-3)",
            },
            td: {
              borderColor: "var(--mantine-color-slate-3)",
              backgroundColor: "#fff",
              verticalAlign: "middle",
            },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "58%" }}>PLATFORM ACTION</Table.Th>
              <Table.Th style={{ width: "42%", textAlign: "center" }}>CREDITS USED</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <Text fz="sm" fw={700} c="slate.9">
                    {row.title}
                  </Text>
                  <Text fz="xs" c="slate.5" mt={4} lh={1.5}>
                    {row.description}
                  </Text>
                </Table.Td>
                <Table.Td>{creditsCell(row)}</Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr
              style={{
                backgroundColor: "var(--mantine-color-primary-0)",
              }}
            >
              <Table.Td style={{ backgroundColor: "var(--mantine-color-primary-0)", borderColor: "var(--mantine-color-slate-3)" }}>
                <Text fz="sm" fw={700} c="primary.8">
                  Total credits per full partnership journey
                </Text>
              </Table.Td>
              <Table.Td style={{ backgroundColor: "var(--mantine-color-primary-0)", borderColor: "var(--mantine-color-slate-3)" }}>
                <Flex justify="center" align="center" gap={10} wrap="wrap">
                  <Text fz="sm" fw={600} c="slate.5" td="line-through">
                    {legacyJourneyTotalStrikethrough}
                  </Text>
                  <Text fz="sm" fw={700} c="primary.7">
                    {totalJourney} Credits
                  </Text>
                </Flex>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>

      <Paper
        radius={12}
        mt="lg"
        px="lg"
        py="md"
        withBorder
        styles={{
          root: {
            backgroundColor: "#FFFBF0",
            borderColor: "#F59E0B",
          },
        }}
      >
        <Flex gap="sm" align="flex-start">
          <ThemeIcon size="sm" variant="transparent" style={{ color: "#D97706", flexShrink: 0 }}>
            <IconAlertCircle size={22} stroke={1.75} />
          </ThemeIcon>
          <Text fz="sm" c="#78350F" lh={1.55} style={{ flex: 1 }}>
            Credits are only deducted for{" "}
            <Text span fw={700} c="#C2410C">
              Verification & Activation ({verificationCredits} credit{verificationCredits !== 1 ? "s" : ""})
            </Text>{" "}
            and{" "}
            <Text span fw={700} c="#C2410C">
              Engagement Activation ({engagementCredits} credits)
            </Text>
            . All other actions are always free.
          </Text>
        </Flex>
      </Paper>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" mt="lg">
        <Paper
          radius={12}
          p="lg"
          withBorder
          style={{ borderColor: "var(--mantine-color-primary-4)", backgroundColor: "#fff" }}
        >
          <Text fz="md" fw={700} c="slate.9">
            All paid plans include
          </Text>
          <Text fz="sm" c="slate.6" mt={6} mb="md">
            Every credit pack comes with the full feature set.
          </Text>
          <Stack gap="sm">
            {paidPlanFeatures.map((line) => (
              <Flex key={line} gap="sm" align="flex-start">
                <ThemeIcon size={22} radius="xl" color="primary" variant="filled" style={{ flexShrink: 0 }}>
                  <IconCheck size={14} stroke={2.5} color="#fff" />
                </ThemeIcon>
                <Text fz="sm" c="slate.8" lh={1.5}>
                  {line}
                </Text>
              </Flex>
            ))}
          </Stack>
        </Paper>

        <Paper
          radius={12}
          p="lg"
          withBorder
          style={{
            borderColor: "#86EFAC",
            backgroundColor: "#F0FDF4",
          }}
        >
          <Badge color="green" variant="filled" size="sm" mb="sm" tt="none" fw={700}>
            100% FREE
          </Badge>
          <Text fz="md" fw={700} c="slate.9">
            Included on the Platform
          </Text>
          <Text fz="sm" c="slate.6" mt={6} mb="md">
            No credit needed. Always available.
          </Text>
          <Stack gap="sm">
            {platformFreeFeatures.map((line) => (
              <Flex key={line} gap="sm" align="flex-start">
                <ThemeIcon
                  size={22}
                  radius="xl"
                  variant="transparent"
                  style={{ backgroundColor: "#DCFCE7", color: "#16A34A", flexShrink: 0 }}
                >
                  <IconCheck size={14} stroke={2.5} />
                </ThemeIcon>
                <Text fz="sm" c="slate.8" lh={1.5}>
                  {line}
                </Text>
              </Flex>
            ))}
          </Stack>
          <Divider my="md" color="#BBF7D0" />
          <Text fz="sm" c="slate.7" lh={1.55}>
            Credits are only used when you{" "}
            <Text span fw={700} c="slate.9">
              initiate a verified connection
            </Text>{" "}
            with another company — everything else is on us.
          </Text>
        </Paper>
      </SimpleGrid>
    </Box>
  );
}

function BuyCreditsFaqSection({ onGetStarted }: { onGetStarted: () => void }) {
  const cards = [
    {
      Icon: IconStack2,
      title: "Stay in control of your budget",
      description:
        "Purchase credit packs upfront and only spend them when you take key actions—so there's no surprise charges as you explore the platform.",
    },
    {
      Icon: IconSparkles,
      title: "Flexible to your needs",
      description:
        "Buy multiple packs over time as your company grows, enters new teams or geographies, or explores different partnership paths.",
    },
    {
      Icon: IconShieldCheck,
      title: "Use across the full journey",
      description:
        "Apply credits to verification, engagement, NDA-related steps, messaging, and documents so your teams can move from first connection to active collaboration.",
    },
  ] as const;

  return (
    <Box maw={1100} mx="auto" mt={48} pb="xl" px={{ base: 0, sm: 4 }}>
      <Text component="h2" fz={26} fw={700} c="slate.9" ta="center" m={0}>
        Why buy credits?
      </Text>
      <Text ta="center" c="slate.6" maw={560} mx="auto" mt="md" fz="sm" lh={1.65}>
        Credits help you unlock verified partnerships and collaborations with predictable, upfront pricing.
      </Text>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg" mt="xl">
        {cards.map(({ Icon, title, description }) => (
          <Paper
            key={title}
            radius={12}
            p={0}
            withBorder
            style={{
              borderColor: "var(--mantine-color-slate-3)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Box
              px="md"
              pt="xl"
              pb="lg"
              style={{
                backgroundColor: "var(--mantine-color-primary-0)",
                textAlign: "center",
              }}
            >
              <Flex justify="center" mb="md">
                <Box
                  w={56}
                  h={56}
                  style={{
                    borderRadius: 12,
                    backgroundColor: "var(--mantine-color-primary-2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon size={28} stroke={1.5} style={{ color: "var(--mantine-color-primary-8)" }} />
                </Box>
              </Flex>
              <Text fz="md" fw={700} c="slate.9" lh={1.35}>
                {title}
              </Text>
            </Box>
            <Box
              p="lg"
              pt="lg"
              style={{ flex: 1, display: "flex", flexDirection: "column", backgroundColor: "#fff" }}
            >
              <Text fz="sm" c="slate.6" ta="center" lh={1.65} style={{ flex: 1 }}>
                {description}
              </Text>
              <Button
                fullWidth
                mt="xl"
                radius="md"
                variant="outline"
                color="primary"
                onClick={onGetStarted}
                styles={{
                  root: {
                    borderWidth: 1,
                    borderColor: "var(--mantine-color-primary-6)",
                    color: "var(--mantine-color-primary-6)",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Get Started
              </Button>
            </Box>
          </Paper>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default function BuyCreditsPage() {
  const navigate = useNavigate();
  const { userDetails } = useUserContext();
  const pricingPlansRef = useRef<HTMLDivElement>(null);
  const orderSummaryRef = useRef<HTMLDivElement>(null);
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [checkingOutPackId, setCheckingOutPackId] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  const currency = getCurrencyForCountry(userDetails?.company?.headquartersCountry);

  useEffect(() => {
    return () => {
      cleanupRazorpay();
    };
  }, []);

  useEffect(() => {
    trpc.paymentConfig.get
      .query()
      .then((data) => setConfig(data as PaymentConfig))
      .catch(handleTrpcError)
      .finally(() => setLoadingConfig(false));
  }, []);

  const verificationCredits = config?.verificationFeeCredits ?? 1;
  const collaborationCredits = config?.engagementFeeCredits ?? 14;

  const creditPacks: CreditPackDef[] =
    config === null
      ? []
      : [
          {
            id: "starter",
            credits: 20,
            tierTitle: "Starter",
            subtitle: "Great for trying it out",
            icon: IconStar,
            featured: false,
          },
          {
            id: "growth",
            credits: 50,
            tierTitle: "Growth",
            subtitle: "For scaling teams",
            icon: IconTrendingUp,
            featured: true,
          },
          {
            id: "scale",
            credits: 100,
            tierTitle: "Scale",
            subtitle: "Maximum value & volume",
            icon: IconBolt,
            featured: false,
          },
        ];

  const starterEffectivePpc =
    creditPacks.length > 0 ? getPackTotalMajor(creditPacks[0].credits, currency) / creditPacks[0].credits : 0;

  const selectedPack = creditPacks.find((p) => p.id === selectedPackId) ?? null;
  const count = selectedPack?.credits ?? 0;
  const total = selectedPack ? getPackTotalMajor(selectedPack.credits, currency) : 0;
  const avgPerCredit = count > 0 ? total / count : 0;

  const selectPack = (packId: string) => {
    setSelectedPackId(packId);
    requestAnimationFrame(() => {
      orderSummaryRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const packDetailLine = (pack: CreditPackDef) => {
    const packTotalMajor = getPackTotalMajor(pack.credits, currency);
    const ppc = pack.credits > 0 ? packTotalMajor / pack.credits : 0;
    const perCreditStr = `${formatPrice(ppc, currency)} per credit.`;

    if (pack.id === creditPacks[0]?.id) {
      return (
        <Text fz="sm" c={pack.featured ? "rgba(255,255,255,0.85)" : "slate.6"} lh={1.5}>
          {perCreditStr} No discount.
        </Text>
      );
    }

    const computedSavingsPct =
      starterEffectivePpc > 0 && ppc < starterEffectivePpc - 1e-9
        ? Math.round((1 - ppc / starterEffectivePpc) * 1000) / 10
        : null;
    const savingsPct =
      pack.id === "scale" && computedSavingsPct != null && computedSavingsPct > 0
        ? 20
        : computedSavingsPct;

    if (savingsPct != null && savingsPct > 0) {
      return (
        <Text fz="sm" c={pack.featured ? "rgba(255,255,255,0.85)" : "slate.6"} lh={1.5}>
          {perCreditStr} Save{" "}
          <Text span c={pack.featured ? "#BBF7D0" : "#16A34A"} fw={700}>
            {savingsPct}% Approx.
          </Text>{" "}
          vs Starter.
        </Text>
      );
    }

    return (
      <Text fz="sm" c={pack.featured ? "rgba(255,255,255,0.85)" : "slate.6"} lh={1.5}>
        {perCreditStr}
      </Text>
    );
  };

  const handleCheckout = async (packId: string, count: number) => {
    if (!count || count < 1) return;
    setCheckingOutPackId(packId);

    if (!window.Razorpay) {
      const isLoaded = await new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });

      if (!isLoaded) {
        errorNotification({
          title: "Error",
          message: "Failed to load payment gateway. Please check your connection.",
        });
        setCheckingOutPackId(null);
        return;
      }
    }

    try {
      const order = await trpc.payment.createTopUpOrder.mutate({ credits: count });

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: "GTsetu",
        description: `Purchase ${order.credits} Credits`,
        order_id: order.orderId,
        prefill: order.prefill,
        theme: { color: "#2563EB" },
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
          razorpay_invoice_id: string;
          razorpay_invoice_receipt: string;
          razorpay_invoice_status: string;
        }) => {
          setVerifying(true);
          try {
            const result = await trpc.payment.verifyTopUpPayment.mutate({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_invoice_id: response.razorpay_invoice_id,
              razorpay_invoice_receipt: response.razorpay_invoice_receipt,
              razorpay_invoice_status: response.razorpay_invoice_status,
              razorpay_signature: response.razorpay_signature,
            });

            if (result.credited) {
              successNotification({
                title: "Payment successful",
                message: `${result.creditsAdded} credits have been added to your account.`,
              });
            } else {
              notifications.show({
                title: "Payment received",
                message: result.message || "Your credits are being processed and will appear shortly.",
                color: "blue",
                position: "top-right",
              });
            }
            navigate("/credits");
          } catch {
            notifications.show({
              title: "Verification issue",
              message:
                "Your payment was received but we couldn't confirm it immediately. Your credits will appear shortly.",
              color: "yellow",
              position: "top-right",
            });
            navigate("/credits");
          } finally {
            setVerifying(false);
            setCheckingOutPackId(null);
            setTimeout(cleanupRazorpay, 500);
          }
        },
        modal: {
          ondismiss: () => {
            setCheckingOutPackId(null);
            setTimeout(cleanupRazorpay, 500);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response: any) => {
        const errorDescription = response?.error?.description || "Your payment could not be processed.";
        console.error("[PAYMENT_FAILED]", response?.error);
        errorNotification({
          title: "Payment failed",
          message: `${errorDescription} Please try again.`,
        });
        setCheckingOutPackId(null);
        setTimeout(cleanupRazorpay, 500);
      });
      rzp.open();
    } catch (err) {
      handleTrpcError(err);
      setCheckingOutPackId(null);
    }
  };

  const primaryBorder = "1px solid var(--mantine-color-primary-3)";

  return (
    <Box mih="100vh">
      <TitleComponent
        title="Purchase credits"
        description="Purchase credits in order to verify company profile and for collaborations."
        leftSection={
          <Button
            variant="outline"
            color="gray"
            p={8}
            onClick={() => navigate("/credits")}
            styles={{
              root: {
                borderColor: "#E5E7EB",
                borderRadius: 8,
                minWidth: "auto",
                height: "auto",
              },
            }}
          >
            <IconArrowLeft size={18} color="#374151" />
          </Button>
        }
      />

      <Container size="lg" px="lg" pb="xl">
        {loadingConfig ? (
          <Center py="xl" mih="40vh">
            <Loader size="lg" />
          </Center>
        ) : (
          <>
            <Flex
              ref={pricingPlansRef}
              gap={{ base: "lg", sm: "md" }}
              justify="center"
              align="stretch"
              direction={{ base: "column", sm: "row" }}
              wrap="nowrap"
              maw={1100}
              mx="auto"
            >
              {creditPacks.map((pack) => {
                const PackIcon = pack.icon;
                const packTotal = getPackTotalMajor(pack.credits, currency);
                const selected = selectedPackId === pack.id;

                return (
                  <Box
                    key={pack.id}
                    pos="relative"
                    style={{ flex: "1 1 0", minWidth: 0 }}
                    pt={pack.featured ? 18 : 0}
                  >
                    {pack.featured && (
                      <Box
                        pos="absolute"
                        top={0}
                        left="50%"
                        style={{ transform: "translateX(-50%)", zIndex: 2, pointerEvents: "none" }}
                      >
                        <Box
                          px="md"
                          py={6}
                          style={{
                            backgroundColor: "#fff",
                            borderRadius: 9999,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                          }}
                        >
                          <Text fz="xs" fw={600} c="primary.6">
                            Most Popular
                          </Text>
                        </Box>
                      </Box>
                    )}

                    <Box
                      h="100%"
                      p="lg"
                      pt={pack.featured ? 28 : "lg"}
                      style={{
                        borderRadius: 12,
                        border: pack.featured ? "none" : primaryBorder,
                        backgroundColor: pack.featured ? "var(--mantine-color-primary-6)" : "#fff",
                        display: "flex",
                        flexDirection: "column",
                        textAlign: "left",
                        outline: selected ? "2px solid var(--mantine-color-primary-6)" : undefined,
                        outlineOffset: selected ? 2 : undefined,
                        cursor: "pointer",
                        boxShadow: selected ? "0 0 0 1px var(--mantine-color-primary-2)" : undefined,
                        transition: "outline-color 0.15s ease, box-shadow 0.15s ease",
                      }}
                      onClick={() => selectPack(pack.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          selectPack(pack.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      aria-label={`${pack.tierTitle} plan, ${pack.credits} credits`}
                    >
                      <Flex justify="space-between" align="flex-start" mb="sm">
                        <ThemeIcon
                          size={44}
                          radius="md"
                          variant="transparent"
                          style={
                            pack.featured
                              ? {
                                  backgroundColor: "rgba(255,255,255,0.2)",
                                  color: "#fff",
                                }
                              : iconBoxGreen
                          }
                        >
                          <PackIcon size={22} stroke={1.75} />
                        </ThemeIcon>
                        {selected && (
                          <ThemeIcon
                            color={pack.featured ? "white" : "primary"}
                            variant={pack.featured ? "outline" : "filled"}
                            size="sm"
                            radius="xl"
                            style={
                              pack.featured
                                ? { borderColor: "rgba(255,255,255,0.9)", color: "#fff" }
                                : undefined
                            }
                          >
                            <IconCheck size={14} stroke={3} />
                          </ThemeIcon>
                        )}
                      </Flex>

                      <Text fz="lg" fw={700} c={pack.featured ? "#fff" : "slate.9"} mb={4}>
                        {pack.tierTitle}
                      </Text>
                      <Text fz="sm" c={pack.featured ? "rgba(255,255,255,0.78)" : "slate.6"} mb="lg">
                        {pack.subtitle}
                      </Text>

                      <Flex align="baseline" gap={6} wrap="wrap" mb={4}>
                        <Text fz={28} fw={800} lh={1.1} c={pack.featured ? "#fff" : "slate.9"}>
                          {formatPrice(packTotal, currency)}
                        </Text>
                        <Text fz="sm" c={pack.featured ? "rgba(255,255,255,0.75)" : "slate.6"}>
                          / pack
                        </Text>
                      </Flex>

                      <Flex align="baseline" gap={6} wrap="wrap" mb="md">
                        <Text fz="md" fw={700} c={pack.featured ? "#fff" : "slate.9"}>
                          {pack.credits} Credit{pack.credits === 1 ? "" : "s"}
                        </Text>
                        <Text fz="sm" c={pack.featured ? "rgba(255,255,255,0.75)" : "slate.6"}>
                          / pack
                        </Text>
                      </Flex>

                      <Box mb="xl" style={{ flex: 1 }}>
                        {packDetailLine(pack)}
                      </Box>

                      <Button
                        fullWidth
                        radius="md"
                        size="md"
                        variant={pack.featured ? "white" : "outline"}
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectPack(pack.id);
                        }}
                        styles={
                          pack.featured
                            ? undefined
                            : {
                                root: {
                                  borderWidth: 1,
                                  borderColor: "var(--mantine-color-primary-6)",
                                  color: "var(--mantine-color-primary-6)",
                                  backgroundColor: "transparent",
                                },
                              }
                        }
                      >
                        Get Started
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Flex>

            <Box
              maw={1100}
              mx="auto"
              mt="xl"
              mb="lg"
              px={{ base: "md", sm: "lg" }}
              py="md"
              style={{
                backgroundColor: "var(--mantine-color-primary-0)",
                borderRadius: 12,
                overflowX: "auto",
              }}
            >
              <Flex gap="sm" align="center" justify="center" wrap="nowrap" w="100%">
                <ThemeIcon size="sm" variant="transparent" color="primary.7" style={{ flexShrink: 0 }}>
                  <IconInfoCircle size={20} stroke={1.5} />
                </ThemeIcon>
                <Text fz="sm" c="primary.7" lh={1.55} ta="center" style={{ whiteSpace: "nowrap" }}>
                  Most companies start with the{" "}
                  <Text span fw={700} c="primary.8">
                    Growth plan
                  </Text>{" "}
                  to initiate multiple verified partnerships simultaneously.
                </Text>
              </Flex>
            </Box>

            <Box ref={orderSummaryRef} maw={480} mx="auto" mt={0} px={{ base: 0, sm: "md" }}>
              <Text fz="sm" fw={500} mb={4}>
                Order summary{" "}
                <Text span c="red" fz="lg">
                  *
                </Text>
              </Text>
              <Text fz="xs" c="slate.6" mb="lg">
                Select a plan above, then review credits and total before checking out.
              </Text>

              <NumberInput
                label="Credits in this purchase"
                placeholder="Select a plan"
                value={selectedPack ? count : ""}
                onChange={() => {}}
                min={1}
                max={10000}
                withAsterisk
                disabled
                hideControls
                mb="lg"
                styles={{ input: { borderRadius: 8 } }}
              />

              <Flex justify="space-between" mb="xs">
                <Text fz="sm" c="slate.6">
                  Avg. per credit ({count || "—"} credits)
                </Text>
                <Text fz="sm" c="slate.6">
                  {selectedPack ? formatPrice(avgPerCredit, currency) : "—"}
                </Text>
              </Flex>

              <Flex justify="space-between" mb="md">
                <Text fz="sm" c="slate.6">
                  Pack subtotal
                </Text>
                <Text fz="sm" c="slate.6">
                  {selectedPack ? formatPrice(total, currency) : "—"}
                </Text>
              </Flex>

              <Divider mb="md" />

              <Flex justify="space-between" mb="xl">
                <Text fz="md" fw={500}>
                  Total
                </Text>
                <Text fz="md" fw={500}>
                  {formatPrice(total, currency)}
                </Text>
              </Flex>

              <Button
                fullWidth
                radius="md"
                onClick={() => {
                  if (!selectedPack) return;
                  void handleCheckout(selectedPack.id, count);
                }}
                loading={selectedPackId !== null && checkingOutPackId === selectedPackId}
                disabled={!count || count < 1}
              >
                {verifying ? "Verifying payment..." : "Check out"}
              </Button>
            </Box>

            <CreditsUsageExplainer
              verificationCredits={verificationCredits}
              engagementCredits={collaborationCredits}
            />

            <BuyCreditsFaqSection
              onGetStarted={() =>
                pricingPlansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
            />
          </>
        )}
      </Container>
    </Box>
  );
}
